/**
 * Permission Utilities
 * Functions for checking user roles and permissions
 */

import { USER_ROLES, PERMISSIONS, ROLE_PERMISSIONS } from '../constants/roles';

/**
 * Check if a user has a specific role
 * @param {Object} user - User object with role property
 * @param {string} requiredRole - Role to check
 * @returns {boolean}
 */
export const hasRole = (user, requiredRole) => {
  if (!user || !user.role) return false;
  return user.role === requiredRole;
};

/**
 * Check if a user has one of multiple roles
 * @param {Object} user - User object
 * @param {Array<string>} roles - Array of roles to check
 * @returns {boolean}
 */
export const hasAnyRole = (user, roles = []) => {
  if (!user || !user.role) return false;
  return roles.includes(user.role);
};

/**
 * Check if a user has a specific permission
 * @param {Object} user - User object with role property
 * @param {string} permission - Permission to check
 * @returns {boolean}
 */
export const hasPermission = (user, permission) => {
  if (!user || !user.role) return false;
  
  // Super admin has all permissions
  if (user.role === USER_ROLES.SUPER_ADMIN) return true;
  
  const rolePermissions = ROLE_PERMISSIONS[user.role] || [];
  return rolePermissions.includes(permission);
};

/**
 * Check if a user has all of multiple permissions
 * @param {Object} user - User object
 * @param {Array<string>} permissions - Array of permissions to check
 * @returns {boolean}
 */
export const hasAllPermissions = (user, permissions = []) => {
  if (!user || !user.role) return false;
  
  // Super admin has all permissions
  if (user.role === USER_ROLES.SUPER_ADMIN) return true;
  
  const rolePermissions = ROLE_PERMISSIONS[user.role] || [];
  return permissions.every(permission => rolePermissions.includes(permission));
};

/**
 * Check if a user has any of multiple permissions
 * @param {Object} user - User object
 * @param {Array<string>} permissions - Array of permissions to check
 * @returns {boolean}
 */
export const hasAnyPermission = (user, permissions = []) => {
  if (!user || !user.role) return false;
  
  // Super admin has all permissions
  if (user.role === USER_ROLES.SUPER_ADMIN) return true;
  
  const rolePermissions = ROLE_PERMISSIONS[user.role] || [];
  return permissions.some(permission => rolePermissions.includes(permission));
};

/**
 * Get all permissions for a user's role
 * @param {Object} user - User object
 * @returns {Array<string>}
 */
export const getUserPermissions = (user) => {
  if (!user || !user.role) return [];
  return ROLE_PERMISSIONS[user.role] || [];
};

/**
 * Check if user can access a specific module
 * @param {Object} user - User object
 * @param {string} module - Module name
 * @returns {boolean}
 */
export const canAccessModule = (user, module) => {
  const modulePermissions = {
    'executive-dashboard': [PERMISSIONS.FINANCIAL_VIEW, PERMISSIONS.REPORTS_VIEW],
    'department-management': [PERMISSIONS.DEPARTMENT_VIEW],
    'staff-workflow': [PERMISSIONS.TASKS_VIEW],
    'inventory': [PERMISSIONS.INVENTORY_VIEW],
    'user-management': [PERMISSIONS.USERS_VIEW],
    'financial-analytics': [PERMISSIONS.FINANCIAL_VIEW],
    'financial-reports': [PERMISSIONS.REPORTS_VIEW],
    'budget-management': [PERMISSIONS.BUDGET_VIEW],
  };
  
  const requiredPermissions = modulePermissions[module];
  if (!requiredPermissions) return false;
  
  return hasAnyPermission(user, requiredPermissions);
};

/**
 * Filter navigation items based on user permissions
 * @param {Object} user - User object
 * @param {Array} navItems - Navigation items array
 * @returns {Array} Filtered navigation items
 */
export const filterNavigationByPermissions = (user, navItems) => {
  if (!user) return [];
  
  return navItems.filter(item => {
    if (!item.requiredPermission && !item.requiredRole) return true;
    
    if (item.requiredPermission) {
      return hasPermission(user, item.requiredPermission);
    }
    
    if (item.requiredRole) {
      return hasRole(user, item.requiredRole);
    }
    
    return false;
  });
};

export default {
  hasRole,
  hasAnyRole,
  hasPermission,
  hasAllPermissions,
  hasAnyPermission,
  getUserPermissions,
  canAccessModule,
  filterNavigationByPermissions,
};
